import React, { useCallback } from 'react'

import {
  classnames, Divider, Icon, InfoOutlined, Tooltip,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import { NOOP } from 'Utils/stableDefaults'

import AllowanceUsage from './AllowanceUsage'
import { TIER } from '../../../constants'
import { allowanceShape } from '../../../propTypes'

// CGTrader AI spends the same allowance on generations only, so the counter keeps its shape
// and swaps the unit noun. No shared "credits" label - see the terminology decision.
const AI_USAGE_KEYS = {
  count: 'ai_generations_used',
  progress: 'progress_label_ai',
  description: 'description_ai',
  pausedTooltip: 'paused_tooltip_ai',
}
const DOWNLOAD_USAGE_KEYS = {
  count: 'models_downloaded',
  progress: 'progress_label',
  description: 'description',
  pausedTooltip: 'paused_tooltip',
}

function UsageTracker({
  downloadsUsed, downloadLimit, periodEndDate = null,
  canceledByAdmin = false, isDisabled = false, isPaused = false, locale = 'en',
  tier = TIER.ESSENTIALS,
  plansRestructureEnabled = false,
  allowance = null,
  isAllowanceInactive = false,
  pausedUntil = null,
  pauseStartsAt = null,
  canResume = false,
  isResumeLoading = false,
  onResume: handleResume = NOOP,
}) {
  const isAiPlan = tier === TIER.AI_STUDIO
  const usageKeys = isAiPlan ? AI_USAGE_KEYS : DOWNLOAD_USAGE_KEYS
  const usagePercentage = downloadLimit > 0
    ? Math.min((downloadsUsed / downloadLimit) * 100, 100)
    : 0

  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )

  if (plansRestructureEnabled && allowance) {
    return (
      <div className="usage-tracker usage-tracker--restructure">
        <div className="cgt-overline-md usage-tracker__header usage-tracker__header--restructure">
          {translate('subscription_management.active.usage.header')}
        </div>
        <AllowanceUsage
          allowance={allowance}
          isInactive={isAllowanceInactive}
          isPaused={isPaused}
          pausedUntil={pausedUntil}
          pauseStartsAt={pauseStartsAt}
          canResume={canResume}
          isResumeLoading={isResumeLoading}
          onResume={handleResume}
          translate={translate}
          locale={locale}
        />
      </div>
    )
  }

  const pausedTooltipText = translate(`subscription_management.active.usage.${usageKeys.pausedTooltip}`)
  const progressLabel = translate(`subscription_management.active.usage.${usageKeys.progress}`, {
    used: downloadsUsed,
    limit: downloadLimit,
  })

  const usageBlock = (
    <>
      <div className="usage-tracker__count-row">
        <span className="cgt-text-medium">
          {translate(`subscription_management.active.usage.${usageKeys.count}`)}
        </span>
        <span className="cgt-text-medium cgt-text-medium--semi-bold">
          {downloadsUsed}/{downloadLimit}
        </span>
      </div>

      <div
        className="usage-tracker__progress-container"
        role="progressbar"
        aria-valuenow={downloadsUsed}
        aria-valuemin={0}
        aria-valuemax={downloadLimit}
        aria-label={isPaused ? `${progressLabel}. ${pausedTooltipText}` : progressLabel}
      >
        <div
          className="usage-tracker__progress-fill"
          style={{ width: `${usagePercentage}%` }}
        />
      </div>
    </>
  )

  return (
    <div className={classnames('usage-tracker', isDisabled && 'usage-tracker--disabled')}>
      <div className="cgt-legacy-text-caption usage-tracker__header">
        {translate('subscription_management.active.usage.header')}
      </div>

      <Divider customClass="usage-tracker__divider" />

      {isPaused ? (
        <Tooltip
          id="usage-tracker-paused"
          content={pausedTooltipText}
          place="bottom"
          tooltipClassName="usage-tracker__paused-tooltip"
        >
          {/* Triangle's trigger span is not focusable, so the block itself carries data-tip/
              data-for: react-tooltip binds its focus/blur handlers to every matching [data-tip]
              node, which makes the tooltip keyboard-reachable. AT users additionally get the
              same text via the progressbar aria-label. */}
          <div
            className="usage-tracker__paused-block"
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            data-tip
            data-for="tooltip-usage-tracker-paused"
            aria-describedby="tooltip-usage-tracker-paused"
          >
            {usageBlock}
          </div>
        </Tooltip>
      ) : usageBlock}

      {!canceledByAdmin && (
        <div className="usage-tracker__reset-row">
          <Icon svgComponent={InfoOutlined} size="small" color="dark" />
          <span className="cgt-legacy-text-tiny">
            {translate('subscription_management.active.usage.reset_date')}{' '}
            <strong>
              {periodEndDate
                ? translate('subscription_management.active.usage.resets_on', { date: periodEndDate })
                : translate('subscription_management.active.usage.no_reset_date')}
            </strong>
          </span>
        </div>
      )}

      <p
        className="cgt-legacy-text-tiny usage-tracker__description"
        // Translation HTML is sourced from local bundles and limited to trusted copy updates.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: translate(`subscription_management.active.usage.${usageKeys.description}`, {
            limit: downloadLimit,
          }),
        }}
      />

      {isAiPlan && (
        <p className="cgt-legacy-text-tiny usage-tracker__exclusion">
          {translate('subscription_management.active.usage.no_marketplace_downloads')}
        </p>
      )}
    </div>
  )
}

UsageTracker.propTypes = {
  downloadsUsed: number.isRequired,
  downloadLimit: number.isRequired,
  periodEndDate: string,
  canceledByAdmin: bool,
  isDisabled: bool,
  isPaused: bool,
  locale: string,
  tier: string,
  plansRestructureEnabled: bool,
  allowance: allowanceShape,
  isAllowanceInactive: bool,
  pausedUntil: string,
  pauseStartsAt: string,
  canResume: bool,
  isResumeLoading: bool,
  onResume: func,
}

export default UsageTracker
